import { useState, useMemo, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { useUserTokens, useUserTrackFilters } from "../../features/users/useUsers";
import Pagination from "../../components/Pagination";
import RowPerPage from "../../components/RowPerPage";
import Activity from "../../components/Activity";

// Helper to process user tokens
const processUsers = (usertokens) => {
  const list = usertokens.map((user) => {
    const tokens = user.tokens || [];
    const sorted = [...tokens].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return {
      ...user,
      lastLogin: sorted[0]?.createdAt || null,
      sessionCount: tokens.length,
      tokens: sorted.map((t) => ({
        id: t._id,
        timestamp: t.createdAt,
        ip: t?.registerIpAddress ? t.registerIpAddress === "::1" ? "DEVELOPMENT_MODE" : t.registerIpAddress.replace(/^::ffff:/, "") : "Not Provided",
        login_app: t.login_app || "Unknown",
        status: t.status || "Success",
        login_ip: t.login_ip || "Not Provided",
      })),
    };
  });
  return list.sort((a, b) => {
    if (!a.lastLogin) return 1;
    if (!b.lastLogin) return -1;
    return new Date(b.lastLogin) - new Date(a.lastLogin);
  });
};

const UserTable = ({ data, isLoading, rowsPerPage, openDetails, isDarkMode, theme }) => {
  const navigate = useNavigate();
  return (
    <Table
      className="min-w-full divide-y"
      style={{ borderColor: theme.palette.divider }}
    >
      <TableHead>
        <TableRow
          sx={{
            backgroundColor: isDarkMode
              ? "#171717"
              : "background.neutral",
            borderRadius: 2,
          }}
        >
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }}>User</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }}>Last Login</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }} className="text-center">App Name</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }} className="text-center">Location</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }} className="text-center">Method</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }} className="text-center">Sessions</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }} className="text-center">Register IP</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.2rem", py: 1.5, padding: 3 }} className="text-center">Details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          Array.from({ length: rowsPerPage }).map((_, index) => (
            <TableRow key={index}>
              <TableCell className="px-6 py-4 whitespace-nowrap">
                <Box className="flex items-center">
                  <Skeleton variant="circular" width={32} height={32} />
                  <Box className="ml-4">
                    <Skeleton variant="text" width={200} height={20} />
                    <Skeleton variant="text" width={100} height={16} />
                  </Box>
                </Box>
              </TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap"><Skeleton variant="text" width={150} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={100} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={120} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={80} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="rectangular" width={40} height={24} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={120} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="circular" width={20} height={20} /></TableCell>
            </TableRow>
          ))
        ) : data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-4">No login records found</TableCell>
          </TableRow>
        ) : (
          data.map((u) => (
            <TableRow key={u._id}>
              <TableCell className="flex items-center px-6 py-4">
                <Box className="flex">
                  {u.profilePicture ? (
                    <Box component="img" src={u.profilePicture} alt={u.email} sx={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <Avatar sx={{ width: 40, height: 40 }}>{u.email.charAt(0).toUpperCase()}</Avatar>
                  )}
                  <Box className="ml-4">
                    <Typography variant="body2">{u.email}</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="textSecondary">{u.firstName} {u.lastName}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/users?userId=${u._id}`)}
                        sx={{ color: theme.palette.primary.main, p: 0.5 }}
                        title="View User Details"
                      >
                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </TableCell>
              <TableCell className="px-6 py-4">
                <Typography variant="body2" color="textSecondary">
                  {u.sessions[0].createdAt ? new Date(u.sessions[0].createdAt).toLocaleString() : "Never logged in"}                </Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">{u.sessions?.[0]?.login_app || "Unknown"}</Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">
                  {u.sessions?.[0]?.IpLookup ? `${u.sessions[0].IpLookup.city || ""}, ${u.sessions[0].IpLookup.country || ""}` : "Unknown"}
                </Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">{u.sessions?.[0]?.login_method || "Unknown"}</Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Button variant="outlined" size="small" color="info">{u.sessions.length}</Button>              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">
                  {u?.registerIpAddress ? u.registerIpAddress === "::1" ? "DEVELOPMENT_MODE" : u.registerIpAddress.replace(/^::ffff:/, "") : "Not Provided"}
                </Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Button size="small" variant="contained" onClick={() => openDetails(u)}>View</Button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

const SearchResultsTable = ({ data, isLoading, isDarkMode, theme }) => {
  return (
    <Table
      className="min-w-full divide-y"
      style={{ borderColor: theme.palette.divider }}
    >
      <TableHead>
        <TableRow
          sx={{
            borderRadius: 2,
          }}
        >
          <TableCell sx={{ fontWeight: 500, fontSize: "1.1rem", py: 1.5, padding: 2 }}>User</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.1rem", py: 1.5, padding: 2 }}>Login Time</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.1rem", py: 1.5, padding: 2 }} className="text-center">App Name</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.1rem", py: 1.5, padding: 2 }} className="text-center">Location</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.1rem", py: 1.5, padding: 2 }} className="text-center">Method</TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: "1.1rem", py: 1.5, padding: 2 }} className="text-center">Login IP</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          Array.from({ length: 10 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell className="px-6 py-4 whitespace-nowrap">
                <Box className="flex items-center">
                  <Skeleton variant="circular" width={32} height={32} />
                  <Box className="ml-4">
                    <Skeleton variant="text" width={150} height={20} />
                  </Box>
                </Box>
              </TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap"><Skeleton variant="text" width={150} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={100} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={120} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={80} height={20} /></TableCell>
              <TableCell className="px-6 py-4 whitespace-nowrap text-center"><Skeleton variant="text" width={120} height={20} /></TableCell>
            </TableRow>
          ))
        ) : data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-4">No matching records found</TableCell>
          </TableRow>
        ) : (
          data.map((item, index) => (
            <TableRow key={item._id || index}>
              <TableCell className="flex items-center px-6 py-4">
                <Box className="flex">
                  {item.user.profilePicture ? (
                    <Box component="img" src={item.user.profilePicture} alt={item.user.email} sx={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32 }}>{item.user.email.charAt(0).toUpperCase()}</Avatar>
                  )}
                  <Box className="ml-4">
                    <Typography variant="body2">{item.user.email}</Typography>
                    <Typography variant="caption" color="textSecondary">{item.user.firstName} {item.user.lastName}</Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell className="px-6 py-4">
                <Typography variant="body2" color="textSecondary">
                  {new Date(item.createdAt).toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">{item.login_app || "Unknown"}</Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">
                  {item.location.city ? `${item.location.city}, ${item.location.country || ""}` : item.location.country || "Unknown"}
                </Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">{item.login_method || "Unknown"}</Typography>
              </TableCell>
              <TableCell className="px-6 py-4 text-center">
                <Typography variant="body2" color="textSecondary">
                  {item.login_ip ? item.login_ip.replace(/^::ffff:/, "") : "Unknown"}
                </Typography>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

const SearchDialog = ({ open, onClose, theme, isDarkMode }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filters, setFilters] = useState({ app: "", method: "", location: "" });

  // Fetch dynamic filters
  const { data: filterOptions = { apps: [], methods: [] } } = useUserTrackFilters();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);
    return () => clearTimeout(handler);
  }, [query]);

  // Fetch data specifically for the search dialog (flat structure)
  // Requesting a large number to get "all" results as requested, or we could handle pagination
  const { data: searchResults = [], isLoading } = useUserTokens(10000, debouncedQuery, filters);

  const handleFilterChange = (field) => (e) => {
    setFilters({ ...filters, [field]: e.target.value });
  };

  const handleClear = () => {
    setQuery("");
    setFilters({ app: "", method: "", location: "" });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Advanced Search</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Query"
              placeholder="Name, Email, IP..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>App Name</InputLabel>
              <Select
                value={filters.app}
                label="App Name"
                onChange={handleFilterChange('app')}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {filterOptions.apps.map((app) => (
                  <MenuItem key={app} value={app}>{app}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Method</InputLabel>
              <Select
                value={filters.method}
                label="Method"
                onChange={handleFilterChange('method')}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {filterOptions.methods.map((method) => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Location"
              placeholder="Country (e.g., United States)"
              value={filters.location}
              onChange={handleFilterChange('location')}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button onClick={handleClear} color="inherit" sx={{ mr: 1 }}>Clear Filters</Button>
          </Grid>
        </Grid>

        <Box sx={{ overflowX: 'auto' }}>
          <SearchResultsTable
            data={searchResults}
            isLoading={isLoading}
            isDarkMode={isDarkMode}
            theme={theme}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default function UserLoginTrackRecord() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  // Main list uses default fetch (no search/filters)
  const { data: usertokens = [], isLoading } = useUserTokens(rowsPerPage);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Memoize processed data for main list
  const processedData = useMemo(() => processUsers(usertokens), [usertokens]);

  const pageItems = useMemo(
    () => processedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [processedData, page, rowsPerPage]
  );

  // Handlers
  const handleChangePage = setPage;
  const handleChangeRows = (n) => {
    setRowsPerPage(n);
    setPage(0);
  };
  const openDetails = (user) => {
    setSelected(user);
    setDetailsOpen(true);
  };
  const closeDetails = () => setDetailsOpen(false);

  const handleExport = () => {
    const headers = [
      "Email",
      "UserID",
      "LastLogin",
      "TotalSessions",
      "RegisterIP",
      "LoginIP",
      "LoginMethod",
      "LoginApp",
      "Location"
    ];
    const rows = processedData.map((u) => [
      u.email,
      u._id,
      u.lastLogin ? new Date(u.lastLogin).toLocaleString() : "Never",
      u.sessionCount,
      u?.registerIpAddress ? u.registerIpAddress === "::1" ? "DEVELOPMENT_MODE" : u.registerIpAddress.replace(/^::ffff:/, "") : "Not Provided",
      u.sessions[0]?.login_ip || "Unknown",
      u.sessions[0]?.login_method || "Unknown",
      u.sessions[0]?.login_app || "Unknown",
      u.sessions[0]?.IpLookup ? `${u.sessions[0].IpLookup.city || ""}, ${u.sessions[0].IpLookup.country || ""}` : "Unknown"
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "login_activity.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Activity />

      <Box
        sx={{
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          minHeight: "80vh",
          color: theme.palette.text.primary,
        }}
        className="p-4 sm:p-6"
      >

        <Box className="flex flex-wrap items-center justify-between mb-6 gap-4">
          <Typography variant="h4" fontWeight="bold">
            Login Activity
          </Typography>
          {/* <RowPerPage rowsPerPage={rowsPerPage} onRowsChange={handleChangeRows} /> */}
          <Box className="flex items-center space-x-3">
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setSearchOpen(true)}
              sx={{ mr: 1 }}
            >
              Search
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Box>
        </Box>

        <Box elevation={isDarkMode ? 2 : 1} sx={{ overflow: "hidden" }}>
          <Box
            className="overflow-x-auto"
            sx={{
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(2),
              borderRadius: 2,
            }}
          >
            <UserTable
              data={pageItems}
              isLoading={isLoading}
              rowsPerPage={rowsPerPage}
              openDetails={openDetails}
              isDarkMode={isDarkMode}
              theme={theme}
            />
          </Box>
          <Pagination
            page={page}
            count={processedData.length}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
          />
        </Box>

        {/* Search Dialog */}
        <SearchDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          openDetails={openDetails}
          theme={theme}
          isDarkMode={isDarkMode}
        />

        {detailsOpen && selected && (
          <Box className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
            <Paper
              sx={{
                width: "100%",
                maxWidth: 600,
                maxHeight: "80vh",
                overflow: "auto",
                backgroundColor: isDarkMode ? "#121212" : "#fff",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
                display="flex"
                justifyContent="space-between"
              >
                <Typography variant="h6">
                  Sessions for: <b> {selected.email}</b>
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={closeDetails}
                >
                  Close
                </Button>
              </Box>
              <Box sx={{ p: 2 }}>
                {selected.sessions.length ? (
                  selected.sessions.map((t) => (
                    <Box
                      key={t.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {new Date(t.createdAt).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          App Name: <b>{t.login_app}</b>
                        </Typography>
                        <br />
                        {t.IpLookup ? (
                          <Typography variant="caption" color="textSecondary">
                            Location:{" "}
                            <b>
                              {t.IpLookup?.city}, {t.IpLookup?.region},{" "}
                              {t.IpLookup?.country}
                            </b>
                          </Typography>
                        ) : null}
                        <br />
                        {t?.login_ip ? (
                          <Typography variant="caption" color="textSecondary">
                            Login Ip: <b>{t?.login_ip}</b>
                          </Typography>
                        ) : null}
                        <br />
                        {t?.login_method ? (
                          <Typography variant="caption" color="textSecondary">
                            Login Method: <b>{t?.login_method}</b>
                          </Typography>
                        ) : null}
                      </Box>
                      <Button
                        // component="span"
                        variant="outlined"
                        sx={{
                          px: 1,
                          py: 0.5,
                          // bgcolor: theme.palette.success.main,
                          // color: "#fff",
                          borderRadius: 1,
                        }}
                      >
                        {/* {t.status} */}
                        Success
                      </Button>
                    </Box>
                  ))
                ) : (
                  <Typography color="textSecondary" align="center">
                    No token sessions found.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </>
  );
}
